import React, { useState } from 'react';
import { User, Edit, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PageHeader } from '../../components/shared/PageHeader';
import { useAuth } from '../../core/contexts/AuthContext';
import { useUsers } from '../../core/hooks/useUsers';

export const ProfilePage: React.FC = () => {
  const { currentUser } = useAuth();
  const { updateUser } = useUsers();
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    displayName: currentUser?.displayName || '',
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    phoneNumber: currentUser?.phoneNumber || '',
    companyRole: currentUser?.companyRole || '',
  });

  const handleSave = async () => {
    if (currentUser?.uid) {
      try {
        await updateUser(currentUser.uid, formData);
        setIsEditMode(false);
      } catch (error) {
        console.error('Failed to update profile:', error);
      }
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile"
        description="Manage your personal information"
        icon={User}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Personal Information</CardTitle>
            {!isEditMode ? (
              <Button onClick={() => setIsEditMode(true)} size="sm">
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditMode(false)}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button onClick={handleSave} size="sm">
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditMode ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Display Name"
                value={formData.displayName}
                onChange={(e) =>
                  setFormData({ ...formData, displayName: e.target.value })
                }
              />
              <Input
                label="First Name"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
              />
              <Input
                label="Last Name"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
              />
              <Input
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) =>
                  setFormData({ ...formData, phoneNumber: e.target.value })
                }
              />
              <Input
                label="Company Role"
                value={formData.companyRole}
                onChange={(e) =>
                  setFormData({ ...formData, companyRole: e.target.value })
                }
                className="md:col-span-2"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{currentUser?.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Display Name</p>
                <p className="font-medium">{currentUser?.displayName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">First Name</p>
                <p className="font-medium">{currentUser?.firstName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Name</p>
                <p className="font-medium">{currentUser?.lastName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Phone Number</p>
                <p className="font-medium">{currentUser?.phoneNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Company Role</p>
                <p className="font-medium">{currentUser?.companyRole || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Role</p>
                <p className="font-medium">{currentUser?.role || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Client ID</p>
                <p className="font-medium">{currentUser?.clientId || currentUser?.organizationId || 'N/A'}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
